import React, { useEffect, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { XR, createXRStore } from '@react-three/xr';
import SealSplash from '../hud/SealSplash';

import Scene from '../three/Scene';
import HandDeck from '../three/VRHandDecks';
import HUD from '../hud/HUD';
import LandingScreen from '../hud/LandingScreen';
import Narrator from '../hud/Narrator';
import TouchControls from '../hud/TouchControls';
import { soundEngine } from '../audio/soundEngine';
import { shared } from '../three/shared';
import { useStore } from '../store';

const xrStore = createXRStore({
  hand: HandDeck,
  controller: HandDeck,
});

export default function Experience() {
  const entered = useStore((s) => s.entered);
  const [vrSupported, setVrSupported] = useState(false);
  const [hideHud, setHideHud] = useState(false);
  // Mount the heavy Canvas only AFTER the splash has had time to paint + dismiss,
  // so the splash's own timers aren't starved by the blocking WebGL init.
  const [mountScene, setMountScene] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMountScene(true), 2400);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('direct') === '1') useStore.getState().launchSimulation();
    if (params.get('enter') === '1') useStore.getState().enter();
    const m = params.get('mode');
    if (m === 'helicopter' || m === 'suv' || m === 'boat') useStore.getState().setMode(m);
    if (params.get('hidehud') === '1') setHideHud(true);
    const photoP = params.get('photo');
    if (photoP === 'faultline' || photoP === 'aerial') { shared.photo = photoP; shared.lowFX = true; }
    if (params.get('lowfx') === '1') shared.lowFX = true;
    if (photoP === 'aerial') { shared.forceStorm = true; useStore.setState({ weather: 'sunny', rainIntensity: 1, stormActive: true, autoCycle: false, timeValue: 0.5 }); shared.env.dayTime = 0.5; }
    if (params.get('tour') === '1') {
      useStore.getState().enter();
      setTimeout(() => useStore.getState().startTour(), 500);
    }
    if (params.get('night') === '1') shared.env.dayTime = 0.02;
    if (params.get('storm') === '1') shared.forceStorm = true;

    // Tour bookmark: reopen an exact zone / time-of-day / weather
    const timeP = params.get('time');
    if (timeP !== null) {
      const tv = parseFloat(timeP);
      if (!isNaN(tv)) { useStore.setState({ autoCycle: false, timeValue: tv }); shared.env.dayTime = tv; }
    }
    const weatherP = params.get('weather');
    if (weatherP && ['sunny', 'mist', 'storm'].includes(weatherP)) useStore.setState({ weather: weatherP });
    const zoneP = params.get('zone');
    if (zoneP !== null) {
      const zi = parseInt(zoneP, 10);
      if (!isNaN(zi)) setTimeout(() => useStore.getState().goToZone(Math.max(0, Math.min(9, zi))), 900);
    }
  }, []);

  useEffect(() => {
    let active = true;
    if (navigator.xr?.isSessionSupported) {
      navigator.xr
        .isSessionSupported('immersive-vr')
        .then((ok) => active && setVrSupported(ok))
        .catch(() => {});
    }
    return () => { active = false; };
  }, []);

  const enterVR = () => {
    if (!entered) useStore.getState().enter();
    soundEngine.start();
    xrStore.enterVR();
  };

  return (
    <div className="App">
      <div className="fixed inset-0" style={{ background: 'radial-gradient(circle at 50% 32%, #0a1410, #04060b 70%)' }} />
      {mountScene && (
        <Canvas
          shadows
          dpr={[1, 1.75]}
          gl={{ antialias: true, powerPreference: 'high-performance' }}
          camera={{ fov: 60, near: 0.1, far: 1300, position: [0, 8, 78] }}
          style={{ width: '100vw', height: '100dvh', display: 'block' }}
        >
          <XR store={xrStore}>
            <Scene />
          </XR>
        </Canvas>
      )}

      {!entered && <LandingScreen onEnterVR={enterVR} vrSupported={vrSupported} />}

      {entered && !hideHud && (
        <>
          <HUD onEnterVR={enterVR} vrSupported={vrSupported} />
          <Narrator />
          <TouchControls />
        </>
      )}

      <SealSplash />
    </div>
  );
}
